import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useRequestPhoneChangeCode,
  useConfirmPhoneChange,
} from '../../hooks/useProfile';
import { useProfileStore } from '../../store/useProfileStore';
import { formatPhoneInput, toE164, isValidPhone } from '../../utils/phoneNumber';
import { Modal } from '../ui/Modal';

interface EditPhoneModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const EditPhoneModal: React.FC<EditPhoneModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation('profile');
  const activeProfile = useProfileStore((s) => s.activeProfile);
  const requestCode = useRequestPhoneChangeCode();
  const confirmChange = useConfirmPhoneChange();

  const [step, setStep] = useState<'send' | 'verify'>('send');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setStep('send');
      setPhone('');
      setCode('');
      setError('');
    }
  }, [isOpen]);

  const e164 = toE164(phone);
  const isSameAsCurrent = !!e164 && e164 === activeProfile?.phoneE164;
  const isSendDisabled =
    requestCode.isPending || !isValidPhone(phone) || isSameAsCurrent;
  const isVerifyDisabled = confirmChange.isPending || code.length < 6;

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSendDisabled || !e164) return;
    setError('');
    try {
      await requestCode.mutateAsync({ phone: e164 });
      setStep('verify');
    } catch (err: unknown) {
      const status = (err as Error & { status?: number }).status;
      if (status === 409) {
        setError(t('contact.phoneModal.errorConflict'));
      } else {
        setError(t('contact.phoneModal.errorSend'));
      }
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isVerifyDisabled) return;
    setError('');
    try {
      await confirmChange.mutateAsync({ code });
      onClose();
    } catch (err: unknown) {
      const status = (err as Error & { status?: number }).status;
      if (status === 410) {
        setError(t('contact.phoneModal.errorExpired'));
        setCode('');
        setStep('send');
      } else {
        setError(t('contact.phoneModal.errorInvalid'));
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" zIndexClass="z-50">
      {step === 'send' ? (
          <>
            <h2 className="text-lg font-bold text-foreground mb-2">
              {t('contact.phoneModal.titleSend')}
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              {t('contact.phoneModal.bodySend')}
            </p>
            <form onSubmit={handleSendCode} className="flex flex-col gap-4">
              <input
                type="tel"
                inputMode="tel"
                value={phone}
                onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                placeholder={t('contact.phoneModal.placeholder')}
                required
                autoFocus
                autoComplete="tel"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              />
              {isSameAsCurrent && (
                <p className="text-muted-foreground text-sm text-center">
                  {t('contact.phoneModal.sameAsCurrent')}
                </p>
              )}
              {error && <p className="text-brand-danger text-sm text-center">{error}</p>}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={requestCode.isPending}
                  className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-lg hover:bg-muted disabled:opacity-50 transition-colors"
                >
                  {t('common:actions.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isSendDisabled}
                  className="px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-lg hover:opacity-90 disabled:opacity-50 transition-colors"
                >
                  {requestCode.isPending
                    ? t('contact.phoneModal.sending')
                    : t('contact.phoneModal.sendCta')}
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            <h2 className="text-lg font-bold text-foreground mb-2">
              {t('contact.phoneModal.titleVerify')}
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              {t('contact.phoneModal.bodyVerify')}
            </p>
            <form onSubmit={handleVerifyCode} className="flex flex-col gap-4">
              <input
                type="text"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                required
                autoFocus
                autoComplete="one-time-code"
                maxLength={6}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              />
              {error && <p className="text-brand-danger text-sm text-center">{error}</p>}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setStep('send');
                    setCode('');
                    setError('');
                  }}
                  disabled={confirmChange.isPending}
                  className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-lg hover:bg-muted disabled:opacity-50 transition-colors"
                >
                  {t('common:actions.back')}
                </button>
                <button
                  type="submit"
                  disabled={isVerifyDisabled}
                  className="px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-lg hover:opacity-90 disabled:opacity-50 transition-colors"
                >
                  {confirmChange.isPending
                    ? t('contact.phoneModal.verifying')
                    : t('contact.phoneModal.verifyCta')}
                </button>
              </div>
            </form>
          </>
        )}
    </Modal>
  );
};
